import { useMemo, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import type { IngestionFilePagesRes } from '@dealstack/shared/lib/generated';
import { SEARCH_DEBOUNCE_MS } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/search.constants';
import { useLibraryDocumentsQuery, useLibraryPageCountsQuery } from '../hooks/useIngestionQueries';
import { LIBRARY_PAGE_SIZE_OPTIONS } from '../ingestion.constants';
import { pagesTowardsCap } from '../ingestion.utils';
import type { LibraryPickerState } from './useLibraryPicker.types';
import { pageSelectionOf, toLibraryQuery, toLibraryRow } from './useLibraryPicker.utils';

/** The document-library picker: search, classification, paging, and a pick that spans pages with its page counts. */
export const useLibraryPicker = (dealId: string, enabled: boolean): LibraryPickerState => {
	const [search, setSearch] = useState('');
	const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
	const [classification, setClassification] = useState<string | null>(null);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(LIBRARY_PAGE_SIZE_OPTIONS[0]);
	// Null until the user changes the pick, so the suggested documents stay pre-checked.
	const [selection, setSelection] = useState<string[] | null>(null);

	const libraryQuery = useLibraryDocumentsQuery(
		dealId,
		toLibraryQuery({ search: debouncedSearch, classification, page, pageSize }),
		enabled
	);
	const data = libraryQuery.data;
	const selectedIds = useMemo(
		() => selection ?? data?.suggestedDocumentIds ?? [],
		[selection, data?.suggestedDocumentIds]
	);

	const pageCountsQuery = useLibraryPageCountsQuery(dealId, selectedIds);
	const countedPages = useMemo(
		() =>
			new Map<string, IngestionFilePagesRes | null>(
				(pageCountsQuery.data?.documents ?? []).map((counted) => [
					counted.documentId,
					counted.pages,
				])
			),
		[pageCountsQuery.data]
	);

	const rows = useMemo(() => {
		const selected = new Set(selectedIds);
		return (data?.documents ?? []).map((document) =>
			toLibraryRow(document, selected, countedPages.get(document.id) ?? null)
		);
	}, [data?.documents, selectedIds, countedPages]);

	const pageSelection = pageSelectionOf(rows);

	const onTogglePage = () => {
		const pageIds = rows.filter((row) => row.selectable).map((row) => row.id);
		setSelection(
			pageSelection === 'all'
				? selectedIds.filter((id) => !pageIds.includes(id))
				: [...new Set([...selectedIds, ...pageIds])]
		);
	};

	return {
		rows,
		isLoading: libraryQuery.isLoading,
		isError: libraryQuery.isError,
		search,
		onSearchChange: (value) => {
			setSearch(value);
			setPage(1);
		},
		classification,
		classifications: data?.classifications ?? [],
		onClassificationChange: (value) => {
			setClassification(value);
			setPage(1);
		},
		page,
		pageCount: Math.max(1, Math.ceil((data?.totalCount ?? 0) / pageSize)),
		pageSize,
		onPageChange: setPage,
		onPageSizeChange: (value) => {
			setPageSize(value);
			setPage(1);
		},
		selectedIds,
		selectedPages: selectedIds.reduce(
			(total, id) => total + pagesTowardsCap(countedPages.get(id) ?? null),
			0
		),
		isCountingPages: pageCountsQuery.isFetching,
		isPageCountError: pageCountsQuery.isError,
		retryPageCounts: () => {
			void pageCountsQuery.refetch();
		},
		pageSelection,
		onToggleRow: (documentId) =>
			setSelection(
				selectedIds.includes(documentId)
					? selectedIds.filter((id) => id !== documentId)
					: [...selectedIds, documentId]
			),
		onTogglePage,
		addToSelection: (documentId) =>
			setSelection(
				selectedIds.includes(documentId) ? selectedIds : [...selectedIds, documentId]
			),
		reset: () => {
			setSearch('');
			setClassification(null);
			setPage(1);
			setSelection(null);
		},
	};
};
